import { useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input } from "@/components/ui/primitives";
import { useAdjustStock } from "./hooks";
import type { Product } from "./api";

export function AdjustStockDialog({
  product,
  onClose,
}: {
  product: Product | null;
  onClose: () => void;
}) {
  const adjust = useAdjustStock();
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!product) return;
    const quantity = Number(delta);
    if (!quantity) {
      setError("Enter a non-zero quantity (use a minus sign to remove stock).");
      return;
    }
    setError(null);
    try {
      await adjust.mutateAsync({ id: product.id, quantity, note: note.trim() || undefined });
      setDelta("");
      setNote("");
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not adjust stock.");
    }
  }

  return (
    <Dialog open={product !== null} onClose={onClose} title={`Adjust stock — ${product?.name ?? ""}`}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          On hand now: <span className="font-medium text-foreground">{product?.quantity_on_hand}</span>
        </p>
        <Field label="Change (+ to add, − to remove)" htmlFor="adj_qty">
          <Input
            id="adj_qty"
            type="number"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. 10 or -3"
          />
        </Field>
        <Field label="Note" htmlFor="adj_note">
          <Input id="adj_note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={adjust.isPending}>
            Apply
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
